import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import { updateCurrentUser } from "@/lib/localAuth";
import { useAuth } from "@/hooks/useAuth";
import { useSyncCode } from "@/hooks/useSyncCode";

type Section = "avatar" | "username" | "sync";

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { code, codeLoading, codeError, fetchOrCreateCode } = useSyncCode();

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarMsg, setAvatarMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [username, setUsername] = useState("");
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [usernameMsg, setUsernameMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [activeSection, setActiveSection] = useState<Section>("avatar");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) {
      setUsername(user.name ?? "");
      setAvatarPreview(user.avatarUrl ?? null);
    }
  }, [user]);

  // Load sync code when sync section is opened (wait for auth to finish first)
  useEffect(() => {
    if (activeSection === "sync" && !code && !codeLoading && !codeError && !loading) {
      fetchOrCreateCode();
    }
  }, [activeSection, code, codeLoading, codeError, fetchOrCreateCode, loading]);

  async function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setAvatarMsg({ type: "error", text: "Please select an image file." });
      return;
    }
    if (file.size > 1.5 * 1024 * 1024) {
      setAvatarMsg({ type: "error", text: "Image must be under 1.5MB." });
      return;
    }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target?.result as string;
      setAvatarPreview(dataUrl);
      const updated = await updateCurrentUser({ avatarDataUrl: dataUrl });
      if (updated) {
        setAvatarMsg({ type: "success", text: "Profile picture updated!" });
      } else {
        setAvatarMsg({ type: "error", text: "Failed to save. Please try again." });
      }
    };
    reader.readAsDataURL(file);
  }

  async function handleUsernameUpdate(e: React.FormEvent) {
    e.preventDefault();
    setUsernameMsg(null);
    const trimmed = username.trim();
    if (!trimmed || trimmed.length < 2) {
      setUsernameMsg({ type: "error", text: "Name must be at least 2 characters." });
      return;
    }
    if (trimmed.length > 8) {
      setUsernameMsg({ type: "error", text: "Name must be 8 characters or less." });
      return;
    }
    setUsernameLoading(true);
    const updated = await updateCurrentUser({ username: trimmed });
    if (updated) {
      setUsername(trimmed);
      setUsernameMsg({ type: "success", text: "Name updated!" });
    } else {
      // Fallback: try updating Supabase directly
      const { supabase } = await import("@/lib/supabase");
      const { data, error } = await supabase.auth.updateUser({ data: { username: trimmed } });
      if (!error && data.user) {
        setUsername(trimmed);
        setUsernameMsg({ type: "success", text: "Name updated!" });
      } else {
        setUsernameMsg({ type: "error", text: error?.message ?? "Failed to update. Please try again." });
      }
    }
    setUsernameLoading(false);
  }

  async function handleCopy() {
    if (!code) return;
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-[#ef4444] text-2xl" />
      </div>
    );
  }

  const initials = user?.name
    ? user.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : "?";

  const sidebarItems: { id: Section; label: string; icon: string }[] = [
    { id: "avatar", label: "Profile Picture", icon: "ri-user-3-line" },
    { id: "username", label: "Display Name", icon: "ri-at-line" },
    { id: "sync", label: "Sync & Transfer", icon: "ri-smartphone-line" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-24 pb-20">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Settings</h1>
          <p className="text-sm text-[#555]">Manage your profile and sync your data across devices</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <aside className="lg:w-52 flex-shrink-0">
            <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-4 mb-4 flex items-center gap-3">
              <div className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#ef4444]">
                    <span className="text-white text-sm font-bold">{initials}</span>
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {user?.name || "My Account"}
                </p>
                <p className="text-[#444] text-xs truncate">
                  {user?.isAnonymous ? "Anonymous user" : (user?.email || "Anonymous user")}
                </p>
              </div>
            </div>

            <nav className="bg-[#111] border border-[#1e1e1e] rounded-xl overflow-hidden">
              {sidebarItems.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveSection(s.id)}
                  className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm text-left transition-colors cursor-pointer whitespace-nowrap border-b border-[#1a1a1a] last:border-0 ${
                    activeSection === s.id
                      ? "text-white bg-[#1a1a1a]"
                      : "text-[#666] hover:text-white hover:bg-[#161616]"
                  }`}
                >
                  <i className={`${s.icon} text-sm w-4 h-4 flex items-center justify-center`} />
                  {s.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main panel */}
          <div className="flex-1 min-w-0">

            {/* Avatar Section */}
            {activeSection === "avatar" && (
              <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                <div className="mb-5">
                  <h2 className="text-white font-semibold text-base mb-1">Profile Picture</h2>
                  <p className="text-[#555] text-sm leading-relaxed">Upload a photo to personalize your account. Max 1.5MB.</p>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                  <div className="w-20 h-20 flex-shrink-0 rounded-full overflow-hidden border-2 border-[#2e2e2e]">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#ef4444]">
                        <span className="text-white text-2xl font-bold">{initials}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-3">
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 bg-[#ef4444] hover:bg-[#dc2626] text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-upload-2-line" />
                      {avatarPreview ? "Change Photo" : "Upload Photo"}
                    </button>
                    <p className="text-[#444] text-xs">JPG, PNG, GIF up to 1.5MB</p>
                  </div>
                </div>
                {avatarMsg && (
                  <p className={`text-xs rounded-md px-3 py-2 mt-4 leading-relaxed ${avatarMsg.type === "success" ? "text-[#22c55e] bg-[#22c55e]/10 border border-[#22c55e]/20" : "text-[#ef4444] bg-[#ef4444]/10 border border-[#ef4444]/20"}`}>
                    {avatarMsg.text}
                  </p>
                )}
              </div>
            )}

            {/* Username Section */}
            {activeSection === "username" && (
              <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                <div className="mb-5">
                  <h2 className="text-white font-semibold text-base mb-1">Display Name</h2>
                  <p className="text-[#555] text-sm leading-relaxed">
                    The name shown in the navbar and across the app.
                  </p>
                </div>
                <form onSubmit={handleUsernameUpdate} className="space-y-4">
                  <div>
                    <label className="text-[#555] text-xs uppercase tracking-widest block mb-1.5">Name</label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Your name"
                      maxLength={8}
                      className="w-full bg-[#1a1a1a] border border-[#2e2e2e] text-white text-sm rounded-md px-3 py-2.5 focus:outline-none focus:border-[#ef4444] placeholder-[#444]"
                    />
                    <p className="text-[#333] text-xs mt-1">{username.length}/8</p>
                  </div>
                  {usernameMsg && (
                    <p className={`text-xs rounded-md px-3 py-2 leading-relaxed ${usernameMsg.type === "success" ? "text-[#22c55e] bg-[#22c55e]/10 border border-[#22c55e]/20" : "text-[#ef4444] bg-[#ef4444]/10 border border-[#ef4444]/20"}`}>
                      {usernameMsg.text}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={usernameLoading}
                    className="flex items-center gap-2 bg-[#ef4444] hover:bg-[#dc2626] text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {usernameLoading && <i className="ri-loader-4-line animate-spin" />}
                    Save Name
                  </button>
                </form>
              </div>
            )}

            {/* Sync Section */}
            {activeSection === "sync" && (
              <div className="space-y-4">
                {/* Your sync code */}
                <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                  <div className="mb-5">
                    <h2 className="text-white font-semibold text-base mb-1">Your Sync Code</h2>
                    <p className="text-[#555] text-sm leading-relaxed">
                      Use this code on a new device to transfer all your data there.
                    </p>
                  </div>

                  {codeLoading ? (
                    <div className="flex items-center gap-2 text-[#555] text-sm">
                      <i className="ri-loader-4-line animate-spin" />
                      Generating your code...
                    </div>
                  ) : codeError ? (
                    <div className="space-y-3">
                      <p className="text-xs text-[#ef4444] bg-[#ef4444]/10 border border-[#ef4444]/20 rounded-md px-3 py-2 leading-relaxed">
                        {codeError}
                      </p>
                      <button
                        onClick={fetchOrCreateCode}
                        className="flex items-center gap-2 text-sm text-[#888] hover:text-white cursor-pointer transition-colors"
                      >
                        <i className="ri-refresh-line" />
                        Try again
                      </button>
                    </div>
                  ) : code ? (
                    <div className="space-y-3">
                      <div className="bg-[#0a0a0a] border border-[#2e2e2e] rounded-lg px-5 py-4 flex items-center justify-between gap-3">
                        <span className="text-white text-2xl font-mono tracking-widest font-bold">{code}</span>
                        <button
                          onClick={handleCopy}
                          className="flex items-center gap-1.5 text-xs text-[#888] hover:text-white transition-colors cursor-pointer whitespace-nowrap px-3 py-1.5 bg-[#1a1a1a] rounded-md"
                        >
                          <i className={copied ? "ri-check-line text-[#22c55e]" : "ri-file-copy-line"} />
                          {copied ? "Copied!" : "Copy"}
                        </button>
                      </div>
                      <p className="text-[#333] text-xs leading-relaxed">
                        Keep this private. Anyone with this code can access your data.
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={fetchOrCreateCode}
                      className="flex items-center gap-2 text-sm text-[#888] hover:text-white cursor-pointer transition-colors"
                    >
                      <i className="ri-refresh-line" />
                      Generate sync code
                    </button>
                  )}
                </div>

                {/* Transfer to this device */}
                <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                  <div className="mb-4">
                    <h2 className="text-white font-semibold text-base mb-1">Transfer from Another Device</h2>
                    <p className="text-[#555] text-sm leading-relaxed">
                      Have a sync code from another device? Go to the transfer page to use it.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/login")}
                    className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#222] border border-[#2e2e2e] text-white text-sm px-4 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-smartphone-line" />
                    Enter a sync code
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
